import React, { ReactElement, useEffect, useState } from 'react';
import Layout from '../../components/UI/Layout';

import { IPageLocationFields, IProductSearchPageFields } from 'types/contentful';
import {
  toSlug,
  BrandedSiteProps,
  getServerSidePropsWithErrors,
  getContentfulPageData,
  cacheDirective,
  buildFullUrl,
  getContentfulSiteData,
  isInvalid,
  withSiteData,
  notFound,
  getContentfulPagesData,
} from '../../lib/routing';
import {
  searchUrl,
  search,
  sortSummary,
  SearchResultWithVariants,
  generateSSCookiesAndHeaders,
} from '../../lib/searchspring';
import { Breadcrumb, SortOption, SearchFacet, FacetType } from '../../lib/searchspring/client';
import {
  DUMMY_URL_BASE,
  handleFilterChange,
  newPathWithQueryParam,
  sortVariants,
} from 'lib/product';

import { useRouter } from 'next/router';
import ProductResults from 'components/UI/Search/ProductResults';
import Pagination from 'components/UI/Search/Pagination';
import { addItemToLocalStorage, getItemFromLocalStorage, possess } from 'lib/util';
import Link from 'components/UI/Link';
import { Asset, Entry } from 'contentful';
import Image from 'components/UI/Image';
import FilterSort from 'components/UI/Search/FilterSort';
import InventoryByLocation from 'components/UI/InventoryByLocation';
import { LOCATION_SELECT_FIELDS } from 'lib/consts';
import { Location } from 'types/common';

type SearchProps = {
  results: SearchResultWithVariants[];
  sortOptions: SortOption[];
  currentSort: SortOption;
  facets: SearchFacet[];
  filters?: Breadcrumb[];
  pageCount: number;
  currentPage: number;
  totalResults: number;
  preview: boolean;
  siteData: BrandedSiteProps;
  currentSlug: string;
  title: string;
  popularBrands: { label: string; slug: string }[];
  categories: string[];
  hero: { heading: string; image: Asset | null };
  locations: Location[];
  showLocationFilterFeature: boolean | null;
};

const SELECTED_BROWSE_LOCATION_KEY = 'selected-browse-location';
const BROWSE_TOGGLE_BUTTON = 'browse-toggle-button';

export default function Search({
  preview,
  currentSlug,
  title,
  results,
  facets,
  currentSort,
  sortOptions,
  siteData,
  pageCount,
  currentPage,
  totalResults,
  popularBrands,
  categories,
  hero,
  locations,
  showLocationFilterFeature,
}: SearchProps): ReactElement {
  const router = useRouter();

  const [selectedLocation, setSlectedLocation] = useState<null | Location>(null);
  const [locationToogle, setLocationToggle] = useState<boolean>(false);
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const [isLocationTooltipOpen, setLocationTooltipOpen] = useState<boolean>(true);

  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'eec.impressionView',
        ecommerce: {
          facets: facets.map((facet) => ({
            name: facet.label,
            active: facet.values.filter((value) => value.active).map((value) => value.label),
          })),
          impressions: results.map((result, idx) => {
            const [, { sku, price, color }] = sortVariants(result.variants)[0];
            return {
              name: result.name,
              item_id: sku,
              price: price,
              variant: color,
              brand: result.brand,
              category: categories.join('>'),
              list: 'Search Results',
              position: idx,
            };
          }),
        },
      });
    }
  });

  useEffect(() => {
    const locationFilter = router.query['filter.ss_location_id'];
    if (showLocationFilterFeature) {
      const savedBrowseLocation = getItemFromLocalStorage(SELECTED_BROWSE_LOCATION_KEY);
      if (savedBrowseLocation) setSlectedLocation(JSON.parse(savedBrowseLocation));
      const savedToggleButton = getItemFromLocalStorage(BROWSE_TOGGLE_BUTTON);
      setLocationToggle(savedToggleButton === 'yes');
      if (locationFilter && savedToggleButton === 'yes') {
        setLocationToggle(true);
      } else {
        setLocationToggle(false);
      }
    } else if (locationFilter) {
      const url = location.origin + location.pathname;
      router.push(url);
    }
  }, [router, router.query, showLocationFilterFeature]);

  const nextSeoProps = {
    description: `View our selection of ${title} available at ${siteData.siteName}. Visit us today to try on different styles and speak with one of our opticians.`,
    title,
  };

  const filterProductsByLocation = (
    location: Location | null,
    toggle: boolean,
    toggleAction = false
  ): void => {
    if (!location) return;

    const active = toggle ? false : true;

    const locationQuery = router.query['filter.ss_location_id'];

    // Do not update the url if the location is already selected and is not from toggle action, incase of same location selected
    if (!toggleAction && location?.locationId === selectedLocation?.locationId && locationQuery)
      return;

    if (locationQuery) {
      const url = new URL(DUMMY_URL_BASE + router.asPath);
      url.searchParams.delete('filter.ss_location_id');
      router.asPath = url.pathname + url.search;
    }

    const url = handleFilterChange(router.asPath, router.query, 'ss_location_id', {
      type: FacetType.VALUE,
      value: location?.locationId,
      active,
      label: location.name,
      count: 0,
    });

    router.push(url);
  };

  const [productType, gender] = categories;

  return (
    <Layout
      siteData={siteData}
      preview={preview}
      nextSeoProps={nextSeoProps}
      currentSlug={currentSlug}
    >
      {/* hero */}
      <div className="flex md:flex-row md:bg-secondary overflow-hidden items-center md:h-72 pt-5 md:pt-0">
        <div className="flex-initial md:self-center">
          <h3 className="font-heading text-4xl md:text-5xl lg:text-6xl md:pl-10 xl:pl-24 pl-4 py-4 text-secondary">
            {hero.heading}
          </h3>
        </div>

        {hero.image && (
          <div className="hidden md:block relative flex-auto">
            <svg
              viewBox="0 0 75 500"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`hidden md:block fill-current absolute left-0 text-secondary-actual z-20 h-full w-20`}
            >
              <ellipse cx="-448" cy="250.5" rx="523" ry="523.5" />
            </svg>
            <Image
              image={hero.image}
              classNames={`w-full h-full object-cover`}
              query="fit=fill&f=center&h=500&w=800"
            />
          </div>
        )}
      </div>

      {/* filters and sort section */}
      <FilterSort
        facets={facets}
        totalResults={totalResults}
        sortOptions={sortOptions}
        currentSort={currentSort}
        setFilterHeight={setFilterHeight}
      />

      {showLocationFilterFeature && (
        <InventoryByLocation
          locations={locations}
          selectedLocation={selectedLocation}
          filterHeight={filterHeight}
          setSlectedLocation={(location: Location) => {
            addItemToLocalStorage(SELECTED_BROWSE_LOCATION_KEY, JSON.stringify(location));
            addItemToLocalStorage(BROWSE_TOGGLE_BUTTON, 'yes');
            filterProductsByLocation(location, true);
            setSlectedLocation(location);
            setLocationToggle(true);
          }}
          setLocationToggle={(toggle: boolean) => {
            addItemToLocalStorage(BROWSE_TOGGLE_BUTTON, toggle ? 'yes' : 'no');
            filterProductsByLocation(selectedLocation, toggle, true);
            setLocationToggle(toggle);
          }}
          locationToogle={locationToogle}
          isLocationTooltipOpen={isLocationTooltipOpen}
          setLocationTooltipOpen={setLocationTooltipOpen}
        />
      )}

      {/* results */}
      <ProductResults
        results={results}
        categories={categories}
        locationName={(locationToogle && selectedLocation?.name) || ''}
        locationZip={(locationToogle && selectedLocation?.zipCode) || ''}
      />
      {results?.length === 0 && (
        <div className="text-center w-64 lg:w-frameNoResult m-auto">
          <img src="/images/frames-not-stock.png" alt="No result found" className="w-fit" />
        </div>
      )}
      {pageCount > 0 && (
        <Pagination
          count={pageCount}
          page={currentPage}
          domain={siteData.domain}
          hrefFn={(page) => {
            return page !== null
              ? newPathWithQueryParam(router.asPath, 'page', page.toString())
              : undefined;
          }}
        />
      )}

      {popularBrands.length > 0 && (
        <div className="p-4 md:px-10 lg:px-16 lg:py-12">
          <h2 id="popular-brands-header" className="text-lg lg:text-2xl font-heading">
            {gender ? possess(gender) : ''} {productType} from Popular Designer Brands
          </h2>
          <ul
            aria-labelledby="popular-brands-header"
            className="flex flex-wrap w-full md:w-5/6 lg:w-3/4 mt-4"
          >
            {popularBrands.map((brand, i) => {
              return (
                <li key={i} className="w-1/2 md:w-1/3 my-2">
                  <Link slug={brand.slug}>
                    <a className="text-sm md:text-lg underline">{brand.label}</a>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Layout>
  );
}

export const getServerSideProps = getServerSidePropsWithErrors<SearchProps>(
  async ({ resolvedUrl, params, query, req, res, preview }) => {
    const slug = toSlug(params?.slug) || 'shop';
    const queryOptions = {
      'fields.slug': slug,
      content_type: 'product-search-page',
      include: 2,
    };

    const host = req.headers.host;
    const locationFields = `${LOCATION_SELECT_FIELDS},fields.locationId`;
    const locationQueryOptions = {
      'fields.site.sys.contentType.sys.id': 'site',
      'fields.site.fields.id': host,
      'fields.locationId[exists]': true,
      limit: 1000,
      select: locationFields,
      content_type: 'pageLocation',
    };

    const siteData = await getContentfulSiteData({
      req,
      res,
      preview,
      resolvedUrl,
    });

    if (isInvalid(siteData)) {
      return siteData;
    }

    const { showLocationFilterFeature } = siteData;

    let locations: Entry<IPageLocationFields>[] = [];
    if (showLocationFilterFeature) {
      const contentfulLocationData = await getContentfulPagesData<IPageLocationFields>({
        res,
        req,
        preview,
        queryOptions: locationQueryOptions,
      });
      if (isInvalid(contentfulLocationData)) {
        return withSiteData(contentfulLocationData, siteData);
      }

      locations = contentfulLocationData.items;
    }

    const contentfulData = await getContentfulPageData<IProductSearchPageFields>({
      res,
      req,
      preview,
      queryOptions,
    });

    if (isInvalid(contentfulData)) {
      return withSiteData(contentfulData, siteData);
    }

    if (!siteData.ecommIds) {
      return withSiteData(notFound(), siteData);
    }

    const searchspringSiteId = siteData.ecommIds.fields.searchSpringId;

    const page = contentfulData.item;

    const categoryFields = page.fields.category.fields;
    const hero = {
      heading: page.fields.heading,
      image: page.fields.category.fields.image || null,
    };

    query.siteId = searchspringSiteId;
    const headers = generateSSCookiesAndHeaders(req, res);
    const searchSummary = await search(
      searchUrl(
        query,
        [`categories_hierarchy=${categoryFields.id}`].concat(page.fields.filters || []),
        buildFullUrl(req, resolvedUrl)
      ),
      searchspringSiteId,
      headers
    );

    const title = page.fields.heading;

    const popularBrands = (categoryFields?.popularBrands || []).map((popBrand) => {
      return {
        label: popBrand.fields.heading,
        slug: `shop/${popBrand.fields.slug}`,
      };
    });

    res.setHeader('Cache-Control', cacheDirective(preview));

    return {
      props: {
        preview: preview ? true : false,
        siteData,
        currentSlug: `shop/${slug}`,
        categories: categoryFields.id.split('>'),
        popularBrands,
        showLocationFilterFeature,
        locations: locations.map((loc) => ({
          ...loc.fields,
          sysId: loc.sys.id,
        })),
        title,
        hero,
        ...searchSummary,
        ...sortSummary(searchSummary.sortOptions, query),
      },
    };
  }
);
